import React, { useState, useEffect } from "react";
import {
    Card,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Collapse,
    Divider,
    useMediaQuery,
    useTheme
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { useQueryWithStore } from "react-admin";
import moment from "moment";

import CardIcon from "./CardIcon";

const AlertsList = () => {
    const theme = useTheme();
    const { data: alerts, loaded } = useQueryWithStore({
        type: "getList",
        resource: "logs72",
        payload: {
            filter: { type: "alert" },
            sort: { order: "DESC" },
            pagination: { page: 1, perPage: 100 },
        },
    });

    if (!loaded) return null;
    if (!alerts?.length) return <Typography sx={{ p: 2, fontSize: 14 }}>No alerts found.</Typography>;

    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                width: "100%", 
                maxWidth: 800,  // Prevents excessive stretching on large screens
                minWidth: 350,   // Prevents extreme shrinking
                overflowX: "auto", // Enables scrolling on smaller screens
                mt: 0, 
                boxShadow: 3 
            }}
        >
            <Table>
                <TableHead sx={{ backgroundColor: theme.palette.grey[300] }}>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14 }}>⚠️ Message</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14 }}>Donor #</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: 14 }}>Date</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {alerts.map((record) => (
                        <TableRow 
                            key={record.id}
                            hover
                            sx={{
                                '&:nth-of-type(odd)': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                            }}
                        >
                            <TableCell>{record.message}</TableCell>
                            <TableCell>{record.donor_num}</TableCell>
                            <TableCell>{moment(record.created_date).format("MM/DD/YYYY")}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const GetAlertColor = ({ setColor }) => {
    const { data: alerts, loaded } = useQueryWithStore({
        type: "getList",
        resource: "logs72",
        payload: {
            filter: { type: "alert" },
            sort: { order: "DESC" },
            pagination: { page: 1, perPage: 100 },
        },
    });

    useEffect(() => {
        if (loaded) {
            setColor(alerts?.length > 0 ? "red" : "navy");
        }
    }, [loaded, alerts, setColor]);

    return null;
};

const GetAlertsCount = () => {
    const { data: alerts, loaded } = useQueryWithStore({
        type: "getList",
        resource: "logs72",
        payload: {
            filter: { type: "alert" },
            sort: { order: "DESC" },
            pagination: { page: 1, perPage: 100 },
        },
    });

    if (!loaded) return null;
    return alerts?.length || 0;
};

const AlertsCom = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [alertColor, setAlertColor] = useState("black");
    const isTablet = useMediaQuery("(max-width: 1024px)");  // Detects iPad-sized screens

    return (
        <Box sx={{ 
            width: "100%", 
            maxWidth: isTablet ? "90%" : 700, // Shrinks width slightly on iPads
            minWidth: 350, 
            p: 0 //isTablet ? 1 : 2 // Adjusts padding on tablets for better spacing
        }}>
            <GetAlertColor setColor={setAlertColor} />
            <Card 
                sx={{ 
                    width: "100%", 
                    minWidth: 350, 
                    maxWidth: "100%",
                    borderRadius: 2, 
                    boxShadow: 3, 
                    p: 1, 
                    overflow: "hidden" 
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                        <CardIcon Icon={ErrorIcon} bgColor={alertColor} />
                        <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "14px" }}>
                            Alerts (<GetAlertsCount />)
                        </Typography>
                    </Box>
                    <IconButton 
                        onClick={() => setIsOpen(!isOpen)} 
                        sx={{ fontSize: isTablet ? "24px" : "inherit" }} // Larger button for touch screens
                    >
                        {isOpen ? "▲" : "▼"}
                    </IconButton>
                </Box>
                <Collapse in={isOpen} sx={{ width: "100%" }}>
                    <Divider sx={{ my: 1 }} />
                    <AlertsList />
                </Collapse>
            </Card>
        </Box>
    );
};

export default AlertsCom;
